/* Custom Cart Drawer — fast, universal */
(function () {
  'use strict';

  // ── Config ────────────────────────────────────────
  const REWARDS = [
    { threshold: 5,  freeCount: 2 },
    { threshold: 9,  freeCount: 4 },
    { threshold: 14, freeCount: 7 },
  ];
  const MAX_THRESHOLD = 14;

  // Upsell variant IDs that don't count toward the progress bar
  const UPSELL_VARIANT_IDS = new Set([42458958168296, 40453259591832, 43411968164072, 44214571368680, 47494777241832]);

  // Shopify cart.js doesn't return compare_at_price on line items.
  // We maintain a cache: variant_id → compare_at_price (cents), seeded with known values.
  // Unknown variants are fetched dynamically via /products/{handle}.js and cached.
  const compareCache = new Map([
    [42458958168296, 1598],
    [40453259591832, 799],
    [43411968164072, 6400],
    [44214571368680, 890],
    [47494777241832, 11200],  // 2x Mystery Boxes
  ]);

  // Fetch compare_at_prices for any items not yet in the cache
  function warmCompareCache(items) {
    const handles = [];
    const seen = new Set();
    (items || []).forEach(item => {
      if (!compareCache.has(item.variant_id)) {
        const match = (item.url || '').match(/\/products\/([^?/]+)/);
        if (match && !seen.has(match[1])) {
          seen.add(match[1]);
          handles.push(match[1]);
        }
      }
    });
    if (!handles.length) return Promise.resolve();
    return Promise.all(handles.map(handle =>
      fetch(`/products/${handle}.js`)
        .then(r => r.json())
        .then(p => (p.variants || []).forEach(v => {
          compareCache.set(v.id, v.compare_at_price || 0);
        }))
        .catch(() => {})
    ));
  }

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }
  function esc(s) {
    return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

  // ── Cart API ──────────────────────────────────────
  function cartChange(line, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity }),
    }).then(r => r.json());
  }

  function cartFetch() {
    return fetch('/cart.js').then(r => r.json());
  }

  // ── Intercept ALL /cart/add calls (any page, any source) ──
  // Also strips Shopify's server-side section rendering from the request —
  // that rendering is the main source of slowness; we use /cart.js instead.
  function interceptCartAdd() {
    const _fetch = window.fetch;
    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input
        : input instanceof Request ? input.url : '';

      if (url.includes('/cart/add')) {
        // Strip `sections` from the POST body so the server skips Liquid rendering
        if (init?.body) {
          try {
            const body = JSON.parse(init.body);
            if (body.sections || body.sections_url) {
              delete body.sections;
              delete body.sections_url;
              init = { ...init, body: JSON.stringify(body) };
            }
          } catch (_) {}
        }
      }

      // Open the drawer immediately so it feels instant
      if (url.includes('/cart/add')) {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && !drawer.classList.contains('active')) {
          showAddingState();
          drawer.open(document.getElementById('ccd-bubble'));
        }
      }

      const p = Reflect.apply(_fetch, window, [input, init]);

      if (url.includes('/cart/add')) {
        p.then(res => {
          if (!res.ok) { hideAddingState(); return; }
          cartFetch().then(cart => {
            updateAll(cart);
            pulseBubble();
          });
        }).catch(() => hideAddingState());
      }
      return p;
    };
  }

  // ── Patch CartDrawer.renderContents ──────────────
  // Dawn's default does a slow server section-render after add-to-cart.
  // We replace it: open immediately + lightweight /cart.js fetch.
  // Must wait for cart-drawer.js to define the custom element.
  function patchCartDrawer() {
    const Cls = customElements.get('cart-drawer');
    if (!Cls || Cls.prototype._ccdPatched) return;
    Cls.prototype._ccdPatched = true;
    Cls.prototype.renderContents = function () {
      this.open();
      // updateAll will be triggered by the interceptor; no double-fetch needed
    };
  }

  // ── Bubble pulse animation ────────────────────────
  function pulseBubble() {
    const b = document.getElementById('ccd-bubble');
    if (!b) return;
    b.classList.remove('ccd-pulse');
    void b.offsetWidth; // reflow to restart animation
    b.classList.add('ccd-pulse');
    b.addEventListener('animationend', () => b.classList.remove('ccd-pulse'), { once: true });
  }

  // ── Adding state (shown while /cart/add is in flight) ─
  function showAddingState() {
    const items = document.getElementById('ccd-items');
    if (!items || items.querySelector('.ccd-adding')) return;
    items.insertAdjacentHTML('afterbegin', '<div class="ccd-adding"><div class="ccd-spinner"></div></div>');
  }
  function hideAddingState() {
    document.querySelector('.ccd-adding')?.remove();
  }

  // ── Upsell visibility ─────────────────────────────
  // Hide items already in cart; hide whole section if all hidden or cart empty
  function syncUpsellVisibility(cart) {
    const cartVids = new Set((cart.items || []).map(i => i.variant_id));
    const section  = document.getElementById('ccd-upsell-section');
    const items    = document.querySelectorAll('.ccd-upsell-item');
    let visible    = 0;
    items.forEach(item => {
      const vid = parseInt(item.dataset.variantId);
      const inCart = cartVids.has(vid);
      const isConditional = item.classList.contains('ccd-upsell-conditional');
      if (inCart) {
        item.style.display = 'none';
      } else if (isConditional) {
        // Conditional items keep their current display state — only shown via trigger
        if (item.style.display !== 'none') visible++;
      } else {
        item.style.display = '';
        item.classList.remove('removing');
        visible++;
      }
    });
    if (section) section.style.display = (visible > 0 && cart.item_count > 0) ? '' : 'none';
  }

  // Count only non-upsell items for the progress bar
  function rewardItemCount(cart) {
    return (cart.items || [])
      .filter(i => !UPSELL_VARIANT_IDS.has(i.variant_id))
      .reduce((sum, i) => sum + i.quantity, 0);
  }

  // ── Update helpers ────────────────────────────────
  function updateAll(cart) {
    // Warm the compare cache for any new variants, then update totals
    warmCompareCache(cart.items).then(() => updateCountAndTotal(cart));
    renderItems(cart);
    updateProgress(rewardItemCount(cart));
    updateBubbleCount(cart.item_count);
    syncUpsellVisibility(cart);
    syncHeaderBubble();
  }

  function updateCountAndTotal(cart) {
    const c = document.getElementById('ccd-count');
    if (c) c.textContent = cart.item_count;

    const t = document.getElementById('ccd-total');
    if (t) t.textContent = money(cart.total_price) + ' AUD';

    // Compare total — use cache for compare_at_prices (Shopify cart.js omits them)
    // compareTotal - cart.total_price naturally combines compare_at + all discounts
    // without double-counting: cart.total_price already has every discount deducted
    let compareTotal = 0;
    (cart.items || []).forEach(item => {
      const cap = compareCache.get(item.variant_id) || 0;
      compareTotal += (cap > item.price ? cap : item.price) * item.quantity;
    });

    // Total savings = full RRP minus what they actually pay
    const totalSavings = compareTotal - cart.total_price;
    const showCompare  = totalSavings > 0;

    // Savings badge
    const savingsRow = document.getElementById('ccd-savings-row');
    if (savingsRow) {
      if (showCompare) {
        savingsRow.textContent = 'Save ' + money(totalSavings);
        savingsRow.style.display = '';
      } else {
        savingsRow.style.display = 'none';
      }
    }

    // Compare (original) strikethrough price
    const compareEl = document.getElementById('ccd-compare');
    if (compareEl) {
      if (showCompare) {
        compareEl.textContent = money(compareTotal);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }
  }

  function updateBubbleCount(count) {
    const b = document.getElementById('ccd-bubble-count');
    if (b) b.textContent = count > 0 ? count : '';
  }

  function syncHeaderBubble() {
    fetch('/?section_id=cart-icon-bubble')
      .then(r => r.text())
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const n = doc.querySelector('.shopify-section');
        const e = document.querySelector('#shopify-section-cart-icon-bubble');
        if (e && n) e.innerHTML = n.innerHTML;
      }).catch(() => {});
  }

  // ── Render cart items ─────────────────────────────
  function renderItems(cart) {
    hideAddingState();
    const container = document.getElementById('ccd-items');
    if (!container) return;

    const empty = !cart.items || cart.items.length === 0;
    document.querySelector('.ccd-rewards')?.style.setProperty('display', empty ? 'none' : '');
    document.querySelector('.ccd-footer')?.style.setProperty('display', empty ? 'none' : '');

    if (empty) {
      container.innerHTML = `<div class="ccd-empty"><p>Your cart is empty</p>
        <button class="ccd-empty-link" type="button" onclick="this.closest('cart-drawer').close()">Continue Shopping</button></div>`;
      return;
    }

    container.innerHTML = cart.items.map((item, i) => {
      const line = i + 1;
      const img  = item.image ? `<img class="ccd-item-img" src="${item.image}" alt="${esc(item.title)}" width="72" height="72" loading="lazy">` : '';
      const vari = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="ccd-item-variant">${esc(item.variant_title)}</p>` : '';
      return `<div class="ccd-item" data-line="${line}" data-unit="${item.price}">
        <a href="${item.url}" class="ccd-item-img-link">${img}</a>
        <div class="ccd-item-details">
          <div class="ccd-item-top">
            <span class="ccd-item-title">${esc(item.product_title)}</span>
            <button class="ccd-item-remove" data-line="${line}" aria-label="Remove">
              <svg width="14" height="16" viewBox="0 0 14 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M1 3h12M4 3V1.5h6V3M2 3l.8 11h8.4L12 3"/>
              </svg>
            </button>
          </div>
          ${vari}
          <div class="ccd-item-bottom">
            <div class="ccd-qty">
              <button class="ccd-qty-btn" data-action="minus" data-line="${line}">-</button>
              <span class="ccd-qty-num">${item.quantity}</span>
              <button class="ccd-qty-btn" data-action="plus" data-line="${line}">+</button>
            </div>
            <span class="ccd-item-price">${money(item.final_line_price)}</span>
          </div>
        </div>
      </div>`;
    }).join('');
  }

  // ── Progress bar ──────────────────────────────────
  function updateProgress(count) {
    const fill = document.getElementById('ccd-progress-fill');
    const msg  = document.getElementById('ccd-reward-msg');
    if (!fill || !msg) return;

    fill.style.width = Math.min((count / MAX_THRESHOLD) * 100, 100) + '%';

    const icons = document.querySelectorAll('.ccd-milestone-icon');
    const labels = document.querySelectorAll('.ccd-milestone-label');
    let next = null;
    REWARDS.forEach((r, i) => {
      const wasReached = icons[i]?.classList.contains('reached');
      const done = count >= r.threshold;
      if (icons[i]) {
        // Re-trigger pop animation when newly reached
        if (done && !wasReached) {
          icons[i].classList.remove('reached');
          void icons[i].offsetWidth; // reflow
        }
        icons[i].classList.toggle('reached', done);
      }
      if (labels[i]) labels[i].classList.toggle('reached-label', done);
      if (!done && !next) next = r;
    });

    if (next) {
      const needed = next.threshold - count;
      msg.style.color = '';
      msg.innerHTML = `<strong>ADD ${needed} MORE STICKER${needed === 1 ? '' : 'S'} TO GET ${next.freeCount} FREE</strong>`;
    } else {
      msg.style.color = '#00a040';
      msg.innerHTML = '<strong>🎉 YOU UNLOCKED ALL FREE STICKERS!</strong>';
    }
  }

  // ── Optimistic qty change ─────────────────────────
  function changeQty(line, newQty) {
    const item = document.querySelector(`.ccd-item[data-line="${line}"]`);
    if (!item || item.dataset.pending) return;
    item.dataset.pending = '1';

    // Instant visual update
    const qtyEl   = item.querySelector('.ccd-qty-num');
    const priceEl = item.querySelector('.ccd-item-price');
    const unit    = parseInt(item.dataset.unit, 10) || 0;
    if (qtyEl) qtyEl.textContent = newQty;
    if (priceEl && newQty > 0) priceEl.textContent = money(unit * newQty);

    cartChange(line, newQty)
      .then(cart => {
        if (cart.items != null) updateAll(cart);
        else cartFetch().then(updateAll);
      })
      .catch(() => cartFetch().then(updateAll))
      .finally(() => { delete item.dataset.pending; });
  }

  // ── Event delegation ──────────────────────────────
  function handleClick(e) {
    const minus = e.target.closest('.ccd-qty-btn[data-action="minus"]');
    if (minus) {
      const line = +minus.dataset.line;
      const cur  = parseInt(document.querySelector(`.ccd-item[data-line="${line}"] .ccd-qty-num`)?.textContent || '1');
      changeQty(line, Math.max(0, cur - 1));
      return;
    }

    const plus = e.target.closest('.ccd-qty-btn[data-action="plus"]');
    if (plus) {
      const line = +plus.dataset.line;
      const cur  = parseInt(document.querySelector(`.ccd-item[data-line="${line}"] .ccd-qty-num`)?.textContent || '1');
      changeQty(line, cur + 1);
      return;
    }

    const rem = e.target.closest('.ccd-item-remove');
    if (rem) {
      const item = rem.closest('.ccd-item');
      if (item) item.style.opacity = '0.3';
      cartChange(+rem.dataset.line, 0)
        .then(cart => cart.items != null ? updateAll(cart) : cartFetch().then(updateAll))
        .catch(() => { if (item) item.style.opacity = ''; });
      return;
    }

    const upsell = e.target.closest('.ccd-upsell-add');
    if (upsell) {
      const vid = parseInt(upsell.dataset.variantId);
      if (!vid) return;
      const upsellItem = upsell.closest('.ccd-upsell-item');
      // Determine which stage this upsell is so we know what to reveal next
      // stage-1 (holographic) triggers stage-2 (mystery box); non-conditional triggers stage-1
      const isStage1 = upsellItem.classList.contains('ccd-upsell-stage-1');
      const nextStage = upsellItem.classList.contains('ccd-upsell-conditional')
        ? (isStage1 ? 'ccd-upsell-stage-2' : null)
        : 'ccd-upsell-stage-1';
      upsell.classList.add('adding');
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: vid, quantity: 1, properties: { '_source': 'Cart Upsell' } }),
      })
        .then(() => cartFetch())
        .then(cart => {
          updateAll(cart);
          pulseBubble();
          if (upsellItem) {
            upsellItem.classList.add('removing');
            setTimeout(() => {
              upsellItem.style.display = 'none';
              upsellItem.classList.remove('removing');
              syncUpsellVisibility(cart);
              // Reveal the next stage upsell if not already in cart
              if (nextStage) {
                const next = document.querySelector(`.${nextStage}`);
                const cartVids = new Set((cart.items || []).map(i => i.variant_id));
                if (next && !cartVids.has(parseInt(next.dataset.variantId))) {
                  next.style.display = '';
                  next.classList.remove('ccd-revealing');
                  void next.offsetWidth;
                  next.classList.add('ccd-revealing');
                }
              }
            }, 200);
          }
        })
        .catch(() => {})
        .finally(() => upsell.classList.remove('adding'));
      return;
    }

    if (e.target.closest('.ccd-continue-btn, .ccd-empty-link')) {
      document.querySelector('cart-drawer')?.close();
    }
  }

  // ── Carousel ──────────────────────────────────────
  let slide = 0;
  function initCarousel() {
    const track  = document.querySelector('.ccd-slider-track');
    const slides = document.querySelectorAll('.ccd-slide');
    const dots   = document.querySelectorAll('.ccd-dot');
    if (!track || !slides.length) return;

    const go = i => {
      slide = ((i % slides.length) + slides.length) % slides.length;
      track.style.transform = `translateX(-${slide * 100}%)`;
      dots.forEach((d, j) => d.classList.toggle('active', j === slide));
    };

    const prev = document.querySelector('.ccd-slider-prev');
    const next = document.querySelector('.ccd-slider-next');
    if (prev) { const p = prev.cloneNode(true); prev.replaceWith(p); p.addEventListener('click', () => go(slide - 1)); }
    if (next) { const n = next.cloneNode(true); next.replaceWith(n); n.addEventListener('click', () => go(slide + 1)); }
    dots.forEach((d, i) => d.addEventListener('click', () => go(i)));
  }

  // ── Init ─────────────────────────────────────────
  function init() {
    // Universal cart/add interceptor — MUST run first, before other scripts add their fetch calls
    interceptCartAdd();

    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    drawer.addEventListener('click', handleClick);

    // Prevent wheel + touch scroll from leaking to the background page
    drawer.addEventListener('wheel', (e) => {
      const scrollEl = drawer.querySelector('.ccd-scroll');
      if (!scrollEl) { e.preventDefault(); return; }
      if (scrollEl.contains(e.target) || e.target === scrollEl) {
        const atTop    = scrollEl.scrollTop <= 0 && e.deltaY < 0;
        const atBottom = scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 1 && e.deltaY > 0;
        if (atTop || atBottom) e.preventDefault();
      } else {
        e.preventDefault();
      }
    }, { passive: false });

    let touchStartY = 0;
    drawer.addEventListener('touchstart', (e) => {
      touchStartY = e.touches[0].clientY;
    }, { passive: true });

    drawer.addEventListener('touchmove', (e) => {
      const scrollEl = drawer.querySelector('.ccd-scroll');
      if (!scrollEl) { e.preventDefault(); return; }
      if (scrollEl.contains(e.target) || e.target === scrollEl) {
        const delta    = touchStartY - e.touches[0].clientY;
        const atTop    = scrollEl.scrollTop <= 0 && delta < 0;
        const atBottom = scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 1 && delta > 0;
        if (atTop || atBottom) e.preventDefault();
      } else {
        e.preventDefault();
      }
    }, { passive: false });

    const bubble = document.getElementById('ccd-bubble');
    if (bubble) bubble.addEventListener('click', () => drawer.open(bubble));

    // Seed totals/progress/upsells from live cart on load (don't re-render items)
    cartFetch().then(cart => {
      warmCompareCache(cart.items).then(() => updateCountAndTotal(cart));
      updateProgress(rewardItemCount(cart));
      updateBubbleCount(cart.item_count);
      syncUpsellVisibility(cart);
    });

    // Patch CartDrawer.renderContents once cart-drawer.js has defined it
    // (cart-drawer.js is deferred and appears after us in the HTML — use whenDefined)
    if (customElements.get('cart-drawer')) {
      patchCartDrawer();
    } else {
      customElements.whenDefined('cart-drawer').then(patchCartDrawer);
    }

    // Re-patch after any re-render
    new MutationObserver(() => {
      patchCartDrawer();
    }).observe(drawer.querySelector('#CartDrawer') || drawer, { childList: true });
  }

  // Run immediately (before defer scripts) so interceptor wraps fetch early
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
